<template>
  <div class="sample-table">
    <div class="sample-table__table-header">
      <span>
        <span
          class="px-0"
          v-for="(subtitle, index) in subtitles"
          :key="index"
        >
          <span class="sample-table__subtitle">
            <span :class="subtitle.class"/>
            <span class="ml-2">{{subtitle.title}}</span>
          </span>
        </span>
      </span>
      <span class="sample-table__list-buttons" :style="listButtonsStyle">
        <s-loading-button
          variant="outline-danger"
          :disabled="!selectedSamples.length"
          @click="deleteSamples"
          class="sample-table__list-btn sample-table__generic-btn"
          :loading="loadingDelete"
          >
          <template #icon>
            <s-icon
              name="delete"
              class="mr-2"
            />
          </template>
          {{ $t('actions.delete') }}
        </s-loading-button>
        <b-button
          :disabled="disableEdition"
          @click="goToEditSample"
          variant="outline-secondary"
          class="sample-table__list-btn sample-table__generic-btn"
        >
          <s-icon
            name="edit-paper"
            class="mr-2"
          />
          {{ $t('actions.edit') }}
        </b-button>
        <b-button
          variant="outline-success"
          :disabled="!selectedSamples.length"
          class="sample-table__list-btn sample-table__generic-btn"
          @click="goToMethodsAndDiagnostics"
        >
          {{ $t('page-name.methods-and-diagnostics') }}
        </b-button>
      </span>
    </div>
    <b-col cols="12" class="sample-table__grid px-0">
      <s-table
        bordered
        class="no-align"
        :items="items"
        :fields="fields"
      >
        <template
          v-for="field in fields"
          #[`cell(${field.key})`]="{ item }"
        >
          <span
            :key="field.key"
            :class="{
              'text-warning': highValuesHandler(item, field),
              'text-danger': veryHighValuesHandler(item, field),
              'text-red font-weight-bold': showAlertIcon(item, field)
            }"
            class="text-nowrap"
          >
            {{ valueProxy(item, field) }}
            <i
              v-if="showAlertIcon(item, field)"
              class="fas fa-exclamation-triangle"
              aria-hidden="true"
            />
          </span>
        </template>
        <template v-for="field in fields" #[`head(${field.key})`]>
          <div :key="field.key">
            <b-row class="text-nowrap no-gutters text-center" align-h="center">
              <b-col>
                {{ valueLabel(field.label) }}
              </b-col>
              <b-col v-if="field.tooltip">
                <info-badge
                  :translation-key="field.tooltip"
                />
              </b-col>
            </b-row>
          </div>
        </template>
        <template #head(check)>
          <div class="paged-grid__check-wrapper">
            <b-form-checkbox
              @change="selectAll"
              :checked="selectAllHandler"
            />
          </div>
        </template>
        <template #cell(check)="data">
          <b-form-checkbox
            :checked="checkItemSelected(data.item)"
            @change="toggleItemSelection(data.item, $event)"
          />
        </template>
      </s-table>
    </b-col>
  </div>
</template>
<style lang="sass" scoped>
  @import "src/styles/defaults/mobile-first"
  @import "src/styles/defaults/variables"

  .general-tab-container
    ::v-deep &__table-head
      text-align: center
      min-width: 65px
    ::v-deep &__table-checkbox
      text-align: center
      max-width: 75px
  .sample-table
    ::v-deep
      .table-bordered th, .table-bordered td
        border: 2px solid var(--color-background-light) !important
    &__table-header
      display: flex
      justify-content: space-between
      align-items: flex-end
      margin-bottom: 10px
      +media-xs
        flex-direction: column-reverse
    &__subtitle
      min-width: 135px
      display: inline-flex
      align-items: center
    &__key
      width: 15px
      height: 15px
      background: #CF8701
      display: inline-block
      &-red
        background: var(--color-danger)
    &__list-btn
      margin-left: 5px
    &__generic-btn
      min-height: unset !important
      min-width: unset !important
    &__buttons
      display: inline-flex
      justify-content: space-between
    &__reprocess-btn
      text-transform: none
</style>

<script>
import { mapGetters } from 'vuex';
import InfoBadge from '@/components/icon/InfoBadge';
import { sampleType } from '@/domain/enums/sample';
import ThemeService from '@/services/themeService';
import SampleFields from '@/domain/data/installation/engineering/chromatography/general/sampleFields';
import sampleTable from '@/domain/data/installation/engineering/chromatography/general/sample-table.json';
import {
  formatter as dateTimeFormatter,
} from '@/services/dateTime/datetimeService';
import {
  formats,
  timezones,
} from '@/constants/localizer';

const EMPTY_CELL = '-';

export default {
  name: 'SampleTable',
  components: {
    InfoBadge,
  },
  props: {
    table: {
      type: Array,
      default: () => ([]),
    },
    showCategory: {
      type: Boolean,
      default: false,
    },
    showValues: {
      type: Boolean,
      default: false,
    },
    selectedSamples: {
      type: Array,
      default: () => [],
    },
    selectedSamplesIds: {
      type: Array,
      default: () => [],
    },
    selectAllHandler: {
      type: Boolean,
      default: false,
    },
    loadingDelete: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapGetters([
      'currentFontSize',
    ]),
    listButtonsStyle() {
      const isLargeSizeFont = this.currentFontSize && this.currentFontSize.includes('18');
      const isLargeScreen = ThemeService.isScreenSize('min-md');
      return {
        'white-space': isLargeSizeFont && isLargeScreen ? 'nowrap' : 'normal',
      };
    },
    items() {
      return this.table;
    },
    subtitles() {
      return [
        {
          class: 'sample-table__key',
          title: this.$t('engineering.chromatography.high-value'),
        },
        {
          class: 'sample-table__key sample-table__key-red',
          title: this.$t('engineering.chromatography.very-high-value'),
        },
      ];
    },
    fields() {
      return sampleTable.map(item => ({
        ...item,
        label: this.$t(item.label, item),
      }));
    },
    disableEdition() {
      return this.selectedSamples.length !== 1;
    },
  },
  methods: {
    selectAll(selectAllValue) {
      this.$emit('select-all', selectAllValue);
    },
    checkItemSelected(item) {
      return !!this.selectedSamples.find(selectedSample => selectedSample.id === item.id);
    },
    toggleItemSelection(item, $event) {
      this.$emit('toggle-item-selection', {
        item,
        selected: $event,
      });
    },
    showAlertIcon({ haveDiagnostic }, { key }) {
      return key === SampleFields.SAMPLE_NUMBER && haveDiagnostic;
    },
    veryHighValuesHandler({ fieldsWithVeryHighAlarmLevel }, { key }) {
      if (!fieldsWithVeryHighAlarmLevel || !fieldsWithVeryHighAlarmLevel.length) return '';
      return this.arrayIncludesString(fieldsWithVeryHighAlarmLevel, key);
    },
    highValuesHandler({ fieldsWithHighAlarmLevel }, { key }) {
      if (!fieldsWithHighAlarmLevel || !fieldsWithHighAlarmLevel.length) return '';
      return this.arrayIncludesString(fieldsWithHighAlarmLevel, key);
    },
    arrayIncludesString(array, key) {
      const formattedArray = array.map(item => item.toLowerCase());
      const formattedString = key.toLowerCase();
      return formattedArray.includes(formattedString);
    },
    valueLabel(item) {
      return item || '';
    },
    valueProxy(item, { key }) {
      const itemKey = item[key];
      let formattedValue = itemKey;

      const sampleTypeLabel = {
        [sampleType.USED]: this.$t('enums.sample-type.used'),
        [sampleType.NEW]: this.$t('enums.sample-type.new'),
        [sampleType.RECONDICIONED]: this.$t('enums.sample-type.recondicioned'),
        [sampleType.REGENERATED]: this.$t('enums.sample-type.regenerated'),
      };

      const valuesTypes = {
        sampleDateTime: this.dateMaskFormatterInUTC(itemKey),
        analysisDateTime: this.dateMaskFormatterInUTC(itemKey),
        sampleType: sampleTypeLabel[itemKey],
        date: this.dateMaskFormatterInUTC(itemKey),
        analyzeDate: this.dateMaskFormatterInUTC(itemKey),
      };

      if (typeof formattedValue === 'number') {
        formattedValue = this.$n(formattedValue);
      }

      const result = valuesTypes[key] || formattedValue;
      return result === undefined ? EMPTY_CELL : result;
    },
    dateMaskFormatterInUTC(date) {
      if (!date) {
        return false;
      }
      return dateTimeFormatter(date, formats.DATETIME_SHORT_WITH_SECONDS, timezones.UTC);
    },
    deleteSamples() {
      this.$emit('delete');
    },
    goToEditSample() {
      this.$emit('edit-sample');
    },
    goToMethodsAndDiagnostics() {
      const routeData = this.$router.resolve({
        name: 'clean-view.chromatography.methods-and-diagnostics',
        query: {
          sampleIds: this.selectedSamplesIds,
        },
      });
      window.open(routeData.href, '_blank');
    },
  },
};
</script>
